/**
 * Currency types in the game.
 */
export enum Currency {
  Shards = 'shards',     // Premium currency
  Coins = 'coins',       // Soft currency (earned through gameplay)
}

/**
 * Shop item category.
 */
export enum ShopCategory {
  Energy = 'energy',
  Boosters = 'boosters',
  Cosmetics = 'cosmetics',
  Gacha = 'gacha',
  BattlePass = 'battlepass',
  VIP = 'vip',
  ShardPacks = 'shard_packs',
}

/**
 * Definition for a shop item.
 */
export interface ShopItem {
  id: string;
  name: string;
  description: string;
  category: ShopCategory;
  currency: Currency;
  price: number;
  /** Real-money price in cents (for IAP items). Null if only premium currency. */
  realMoneyPriceCents: number | null;
  /** How many can be purchased (-1 = unlimited). */
  purchaseLimit: number;
  /** Whether this is a one-time purchase. */
  oneTime: boolean;
  /** Whether this item is currently available. */
  available: boolean;
  /** Icon key for rendering. */
  iconKey: string;
}

/**
 * Purchase record.
 */
export interface PurchaseRecord {
  itemId: string;
  timestamp: number;
  price: number;
  currency: Currency;
}

/**
 * Player wallet state.
 */
export interface Wallet {
  shards: number;
  coins: number;
}

/**
 * ShopManager handles the in-game shop logic:
 * wallet management, item catalog, purchase validation, and transaction history.
 */
export class ShopManager {
  private wallet: Wallet;
  private items: Map<string, ShopItem> = new Map();
  private purchases: PurchaseRecord[] = [];
  private purchaseCounts: Map<string, number> = new Map();

  constructor(initialShards: number = 0, initialCoins: number = 0) {
    this.wallet = { shards: initialShards, coins: initialCoins };
    this.initializeShop();
  }

  /**
   * Initialize the default shop catalog.
   */
  private initializeShop(): void {
    const items: ShopItem[] = [
      // Energy refills
      {
        id: 'energy_small',
        name: 'Energy Pack',
        description: 'Restore 10 energy instantly.',
        category: ShopCategory.Energy,
        currency: Currency.Coins,
        price: 50,
        realMoneyPriceCents: null,
        purchaseLimit: -1,
        oneTime: false,
        available: true,
        iconKey: 'icon_energy_small',
      },
      {
        id: 'energy_full',
        name: 'Full Energy Refill',
        description: 'Restore energy to maximum.',
        category: ShopCategory.Energy,
        currency: Currency.Shards,
        price: 10,
        realMoneyPriceCents: null,
        purchaseLimit: -1,
        oneTime: false,
        available: true,
        iconKey: 'icon_energy_full',
      },

      // Boosters
      {
        id: 'booster_2x_merge',
        name: '2x Merge Points (1hr)',
        description: 'Double merge points for 1 hour.',
        category: ShopCategory.Boosters,
        currency: Currency.Shards,
        price: 25,
        realMoneyPriceCents: null,
        purchaseLimit: -1,
        oneTime: false,
        available: true,
        iconKey: 'icon_booster_2x',
      },
      {
        id: 'booster_auto_merge',
        name: 'Auto-Merge (30min)',
        description: 'Automatically merge matching adjacent elements.',
        category: ShopCategory.Boosters,
        currency: Currency.Shards,
        price: 50,
        realMoneyPriceCents: null,
        purchaseLimit: -1,
        oneTime: false,
        available: true,
        iconKey: 'icon_booster_auto',
      },

      // Shard Packs (IAP)
      {
        id: 'shard_pack_small',
        name: '50 Shards',
        description: 'A small pouch of shards.',
        category: ShopCategory.ShardPacks,
        currency: Currency.Shards,
        price: 0, // Free — you're buying the shards
        realMoneyPriceCents: 99,
        purchaseLimit: -1,
        oneTime: false,
        available: true,
        iconKey: 'icon_shard_small',
      },
      {
        id: 'shard_pack_medium',
        name: '250 Shards',
        description: 'A hefty bag of shards. Best value!',
        category: ShopCategory.ShardPacks,
        currency: Currency.Shards,
        price: 0,
        realMoneyPriceCents: 499,
        purchaseLimit: -1,
        oneTime: false,
        available: true,
        iconKey: 'icon_shard_medium',
      },
      {
        id: 'shard_pack_large',
        name: '600 Shards + Bonus',
        description: 'A treasure chest of shards with 100 bonus.',
        category: ShopCategory.ShardPacks,
        currency: Currency.Shards,
        price: 0,
        realMoneyPriceCents: 999,
        purchaseLimit: -1,
        oneTime: false,
        available: true,
        iconKey: 'icon_shard_large',
      },

      // Gacha
      {
        id: 'gacha_single',
        name: 'Element Draw',
        description: 'Draw a random element (Shard tier or higher).',
        category: ShopCategory.Gacha,
        currency: Currency.Shards,
        price: 30,
        realMoneyPriceCents: null,
        purchaseLimit: -1,
        oneTime: false,
        available: true,
        iconKey: 'icon_gacha_single',
      },
      {
        id: 'gacha_ten',
        name: '10x Element Draw',
        description: 'Draw 10 elements with guaranteed Gem or higher.',
        category: ShopCategory.Gacha,
        currency: Currency.Shards,
        price: 270,
        realMoneyPriceCents: null,
        purchaseLimit: -1,
        oneTime: false,
        available: true,
        iconKey: 'icon_gacha_ten',
      },

      // Battle Pass
      {
        id: 'battle_pass',
        name: 'Season Pass',
        description: 'Unlock premium rewards track for this season.',
        category: ShopCategory.BattlePass,
        currency: Currency.Shards,
        price: 0,
        realMoneyPriceCents: 999,
        purchaseLimit: 1,
        oneTime: true,
        available: true,
        iconKey: 'icon_battle_pass',
      },

      // VIP
      {
        id: 'vip_monthly',
        name: 'VIP Membership',
        description: 'Ad-free, 2x idle speed, daily Shards, exclusive cosmetics.',
        category: ShopCategory.VIP,
        currency: Currency.Shards,
        price: 0,
        realMoneyPriceCents: 999,
        purchaseLimit: 1,
        oneTime: false,
        available: true,
        iconKey: 'icon_vip',
      },
    ];

    for (const item of items) {
      this.items.set(item.id, item);
    }
  }

  // ===== WALLET =====

  getWallet(): Wallet {
    return { ...this.wallet };
  }

  getShards(): number {
    return this.wallet.shards;
  }

  getCoins(): number {
    return this.wallet.coins;
  }

  addShards(amount: number): void {
    if (amount < 0) return;
    this.wallet.shards += amount;
  }

  addCoins(amount: number): void {
    if (amount < 0) return;
    this.wallet.coins += amount;
  }

  /**
   * Check if the player can afford an item.
   */
  canAfford(itemId: string): boolean {
    const item = this.items.get(itemId);
    if (!item) return false;
    if (!item.available) return false;

    // Check purchase limits
    if (item.purchaseLimit > 0) {
      const count = this.purchaseCounts.get(itemId) ?? 0;
      if (count >= item.purchaseLimit) return false;
    }

    // IAP items don't use in-game currency
    if (item.realMoneyPriceCents !== null && item.price === 0) {
      return true; // Always "affordable" — real money check is external
    }

    const balance = item.currency === Currency.Shards
      ? this.wallet.shards
      : this.wallet.coins;

    return balance >= item.price;
  }

  /**
   * Purchase an item. Returns true if successful.
   * For IAP items (realMoneyPriceCents), this only validates and records the purchase —
   * actual payment must be verified server-side.
   */
  purchase(itemId: string): boolean {
    const item = this.items.get(itemId);
    if (!item) return false;
    if (!this.canAfford(itemId)) return false;

    // Deduct currency (if applicable)
    if (item.price > 0) {
      if (item.currency === Currency.Shards) {
        this.wallet.shards -= item.price;
      } else {
        this.wallet.coins -= item.price;
      }
    }

    // Record purchase
    const record: PurchaseRecord = {
      itemId,
      timestamp: Date.now(),
      price: item.price,
      currency: item.currency,
    };
    this.purchases.push(record);

    // Update purchase count
    const count = this.purchaseCounts.get(itemId) ?? 0;
    this.purchaseCounts.set(itemId, count + 1);

    return true;
  }

  // ===== CATALOG =====

  getItem(itemId: string): ShopItem | undefined {
    return this.items.get(itemId);
  }

  getItemsByCategory(category: ShopCategory): ShopItem[] {
    return Array.from(this.items.values())
      .filter(item => item.category === category && item.available);
  }

  getAllItems(): ShopItem[] {
    return Array.from(this.items.values()).filter(item => item.available);
  }

  getCategories(): ShopCategory[] {
    const cats = new Set<ShopCategory>();
    for (const item of this.items.values()) {
      if (item.available) cats.add(item.category);
    }
    return Array.from(cats);
  }

  /**
   * Get purchase count for an item.
   */
  getPurchaseCount(itemId: string): number {
    return this.purchaseCounts.get(itemId) ?? 0;
  }

  /**
   * Get full purchase history.
   */
  getPurchaseHistory(): PurchaseRecord[] {
    return [...this.purchases];
  }

  // ===== SAVE/LOAD =====

  exportState(): { wallet: Wallet; purchaseCounts: Record<string, number> } {
    const counts: Record<string, number> = {};
    for (const [id, count] of this.purchaseCounts) {
      counts[id] = count;
    }
    return {
      wallet: { ...this.wallet },
      purchaseCounts: counts,
    };
  }

  loadState(state: { wallet: Wallet; purchaseCounts: Record<string, number> }): void {
    this.wallet = { ...state.wallet };
    this.purchaseCounts.clear();
    for (const [id, count] of Object.entries(state.purchaseCounts)) {
      this.purchaseCounts.set(id, count);
    }
  }
}
